// Device settings for the Classic channel list. #555, #557: Show Apps and Show Collections in the channel list. Both are on by
// default; off, that section leaves the Classic list and the server menu carries it instead (an Apps item, a Collections submenu with
// "New collection"), so it is never out of reach. The Chats layout never shows them in its list: its menu always does. Remembered on
// this device only, like the server banner (bannerPref.ts); the iOS keys are bloom.channelList.showApps and .showCollections.
// Storage can be missing or throw (private windows): then the choice lasts until reload.
//
// #297, the same device, off by default: Flat channel list, Sort by activity (only while flat) and Dense channel list. The iOS and Mac keys
// are bloom.channelList.flat, .byActivity and .dense; the rules below follow ChannelListOptions.swift.
import { useSyncExternalStore } from "react";
import type { Channel } from "@bloom/shared";

export const SHOW_APPS_KEY = "bloom:showApps";
export const SHOW_COLLECTIONS_KEY = "bloom:showCollections";
export const FLAT_KEY = "bloom:channelListFlat";
export const BY_ACTIVITY_KEY = "bloom:channelListByActivity";
export const DENSE_KEY = "bloom:channelListDense";
/** Group idle channels (#596, #601): off by default. The collapse state of the Idle group is stored as "on" (collapsed, the default) or "off". */
export const IDLE_KEY = "bloom:channelListIdle";
export const IDLE_COLLAPSED_KEY = "bloom:channelListIdleCollapsed";

/** A stored value as on or off; anything but "off" is on. */
export const parseShow = (raw: unknown): boolean => raw !== "off";

/** A stored value as on or off, for a setting that is off by default; anything but "on" is off. */
export const parseOn = (raw: unknown): boolean => raw === "on";

/** One on/off setting in localStorage, with a hook that follows it (and another tab's change). `dflt`: what no stored value means. */
function pref(key: string, dflt = true) {
  const parse = dflt ? parseShow : parseOn;
  const stored = (): boolean => {
    try {
      return parse(localStorage.getItem(key));
    } catch {
      return dflt;
    }
  };
  const listeners = new Set<() => void>();
  let current = stored();
  const subscribe = (fn: () => void) => {
    listeners.add(fn);
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key) return;
      current = stored();
      for (const l of listeners) l();
    };
    addEventListener("storage", onStorage);
    return () => {
      listeners.delete(fn);
      removeEventListener("storage", onStorage);
    };
  };
  return {
    set(on: boolean) {
      current = on;
      try {
        localStorage.setItem(key, on ? "on" : "off");
      } catch {
        // private mode: still applies for this page
      }
      for (const l of listeners) l();
    },
    use: (): boolean => useSyncExternalStore(subscribe, () => current),
  };
}

const apps = pref(SHOW_APPS_KEY);
const collections = pref(SHOW_COLLECTIONS_KEY);

export const setShowApps = apps.set;
export const useShowApps = apps.use;
export const setShowCollections = collections.set;
export const useShowCollections = collections.use;

const flat = pref(FLAT_KEY, false);
const byActivity = pref(BY_ACTIVITY_KEY, false);
const dense = pref(DENSE_KEY, false);

export const setFlatList = flat.set;
export const useFlatList = flat.use;
export const setByActivity = byActivity.set;
export const useByActivity = byActivity.use;
export const setDenseList = dense.set;
export const useDenseList = dense.use;

// #596, #601: Group idle channels (off), and whether the Idle group is collapsed (on, so collapsed). Both per device; the rule is
// idleChannels.ts. The iOS keys are bloom.channelList.groupIdle and .idleCollapsed.
const groupIdle = pref(IDLE_KEY, false);
const idleCollapsed = pref(IDLE_COLLAPSED_KEY, true);

export const setGroupIdle = groupIdle.set;
export const useGroupIdle = groupIdle.use;
export const setIdleCollapsed = idleCollapsed.set;
export const useIdleCollapsed = idleCollapsed.use;

/** What the Classic list draws and what its server menu carries, for the two switches (the rule, apart from the views). */
export function classicSections(showApps: boolean, showCollections: boolean) {
  return {
    listApps: showApps,
    listCollections: showCollections,
    menuApps: !showApps,
    menuCollections: !showCollections,
  };
}

/** A name's sort key: case and accents ignored ("Écu" next to "ecu"). */
const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "base" });

/**
 * The flat list (#297): every text channel of every category in one list. A to Z by name, case and accents ignored.
 * `byActivity`: the newest message first, a tie by name, and a channel with no message last, by name. `lastTs` is a channel's
 * newest message time (ms) by its id, undefined when nothing was posted or the time isn't known yet. A tie on the name keeps the
 * channel's id order, so the list never reshuffles between two draws. Returns a new array; the input is left as it was.
 */
export function flatChannels(channels: Channel[], byActivity: boolean, lastTs: (id: string) => number | undefined): Channel[] {
  const text = channels.filter((c) => c.kind !== "dm");
  const times = new Map(text.map((c) => [c.id, lastTs(c.id)]));
  return text.sort((a, b) => {
    if (byActivity) {
      const x = times.get(a.id);
      const y = times.get(b.id);
      if (x !== undefined && y !== undefined && x !== y) return y - x;
      if (x !== undefined && y === undefined) return -1;
      if (x === undefined && y !== undefined) return 1;
    }
    return byName(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
  });
}

/** The newest message time of each channel, from the Chats rows (the poll, and the socket for the open server). No message: no entry. */
export function lastTimes(rows: { channelId: string; lastTs: number | null }[]): Map<string, number> {
  const times = new Map<string, number>();
  for (const r of rows) if (r.lastTs !== null) times.set(r.channelId, Math.max(times.get(r.channelId) ?? r.lastTs, r.lastTs));
  return times;
}

/** The text channels that have a Chats row to read: their newest message time is known, or known to be none. A row still loading is not one. */
export function knownChannels(rows: { channelId: string; kind: string; loading?: boolean }[]): Set<string> {
  return new Set(rows.filter((r) => r.kind !== "dm" && !r.loading).map((r) => r.channelId));
}
